import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import { compressImage } from '../utils/fileCompression';

const initialState = {
  claims: [],
  claim: null,
  loading: false,
  isSuccess: false,
  messages: {},
  errors: [],
  uploadProgress: 0
};

// ========== FETCH CLAIMS ==========
export const fetchClaims = createAsyncThunk(
  'claims/fetchClaims',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim/userClaims', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== FETCH CLAIM BY ID ==========
export const fetchClaimByID = createAsyncThunk(
  'claims/fetchClaimByID',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      if (!id) throw new Error('Invalid ID');
      const response = await api.get(`/claim/getDetailClaims/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

// ========== FETCH CLAIM MOBILE BY ID ==========
export const fetchClaimMobileByID = createAsyncThunk(
  'claims/fetchClaimMobileByID',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      if (!id) throw new Error('Invalid ID');
      const response = await api.get(`/claim/getDetailClaims-Mobile/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

// ========== ADD MESSAGE ==========
export const addMessage = createAsyncThunk(
  'claims/addMessage',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      // Route dédiée au site client : même traitement que /add-message, mais
      // l'événement temps réel envoyé à l'admin porte le vrai id de la réclamation.
      const response = await api.post(`/claim/${id}/add-message-web`, { message });
      dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== ADD MEDIA MESSAGE ==========
export const AddMediaMessage = createAsyncThunk(
  'claim/AddMediaMessage',
  async ({ id, formData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== ADD CLAIM (VERSION AVEC COMPRESSION EN UNE SEULE REQUÊTE) ==========
export const AddClaim = createAsyncThunk(
  'claim/addClaim',
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('Token non trouvé. Veuillez vous reconnecter.');
      }

      const files = formData.getAll('file');
      const claimFormData = new FormData();

      Array.from(formData.entries()).forEach(([key, value]) => {
        if (key !== 'file' && key !== 'files') {
          claimFormData.append(key, value);
        }
      });

      if (files && files.length > 0) {
        const compressedFiles = await Promise.all(
          files.map(async (file) => {
            if (file.type.startsWith('image/')) {
              try {
                const compressedBlob = await compressImage(file, 2);
                const optimizedFile = new File([compressedBlob], file.name, {
                  type: 'image/jpeg'
                });
                return optimizedFile;
              } catch (error) {
                return file;
              }
            }
            return file;
          })
        );

        compressedFiles.forEach((file) => {
          claimFormData.append('file', file);
        });
      }

      const claimResponse = await api.post('/claim', claimFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return claimResponse.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
        return rejectWithValue('Session expirée. Veuillez vous reconnecter.');
      }

      if (error.response?.status === 413) {
        return rejectWithValue('Fichier(s) trop volumineux. Veuillez réduire la taille.');
      }

      return rejectWithValue(
        error.response?.data || error.message || 'Erreur lors de la création du claim'
      );
    }
  }
);
// ========== LIKE CLAIM ==========
export const likeClaim = createAsyncThunk(
  'claim/likeClaim',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.put(`/claim/like-claim/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== DISLIKE CLAIM ==========
export const dislikeClaim = createAsyncThunk(
  'claim/dislikeClaim',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.put(`/claim/dislike-claim/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// ========== DELETE MESSAGE ==========
export const deleteMessage = createAsyncThunk(
  'claims/deleteMessage',
  async ({ claimId, actionId }, { rejectWithValue, dispatch }) => {
    try {
      if (!claimId || !actionId) {
        throw new Error('Les paramètres claimId ou actionId sont manquants');
      }

      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await api.delete(`/claim/${claimId}/delete/${actionId}`, {
        headers
      });

      dispatch(fetchClaimByID(claimId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur inconnue');
    }
  }
);

// ========== MARK ACTIONS VIEWED ==========
export const markActionsViewed = createAsyncThunk(
  'claims/markActionsViewed',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/claim/${claimId}/actions-viewed`);
      return claimId;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

// ========== SHARE MEDIA TO CHAT ==========
export const shareMediaToChat = createAsyncThunk(
  'claims/shareMediaToChat',
  async ({ sourceChatId, targetChatId, fileId }, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('Token non trouvé. Veuillez vous reconnecter.');
      }

      const response = await api.post(
        '/claim/shareMedia',
        {
          sourceChatId,
          targetChatId,
          fileId,
          messageType: 'shared_media'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await dispatch(fetchClaimByID(targetChatId));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue('Session expirée. Veuillez vous reconnecter.');
      }
      return rejectWithValue(error.response?.data || 'Erreur de partage');
    }
  }
);

// ========== SLICE ==========
const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    removeClaimLocally: (state, action) => {
      const id = action.payload;
      state.claims = state.claims.filter((c) => c.id !== id && c._id !== id);
      if (state.claim && (state.claim.id === id || state.claim._id === id)) {
        state.claim = null;
      }
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaims.pending, (state) => { state.loading = true; })
      .addCase(fetchClaims.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claims = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchClaims.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(fetchClaimByID.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimByID.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claim = payload;
        state.loading = false;
      })
      .addCase(fetchClaimByID.rejected, (state) => {
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(addMessage.pending, (state) => { state.loading = true; })
      .addCase(addMessage.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
      })
      .addCase(addMessage.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.loading = false;
        state.errors = payload;
      })

      .addCase(AddMediaMessage.pending, (state) => { state.loading = true; })
      .addCase(AddMediaMessage.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
      })
      .addCase(AddMediaMessage.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.loading = false;
        state.errors = payload;
      })

      .addCase(AddClaim.pending, (state) => {
        state.loading = true;
        state.uploadProgress = 0;
        state.errors = [];
      })
      .addCase(AddClaim.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
        state.uploadProgress = 100;
        if (payload) state.claims.unshift(payload);
      })
      .addCase(AddClaim.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.loading = false;
        state.uploadProgress = 0;
        state.errors = payload;
      })

      .addCase(likeClaim.pending, (state) => { state.loading = true; })
      .addCase(likeClaim.fulfilled, (state, action) => {
        const { payload, meta } = action;
        const claimId = meta.arg;
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
        state.claims = state.claims.filter((c) => c.id !== claimId && c._id !== claimId);
        if (state.claim && (state.claim.id === claimId || state.claim._id === claimId)) {
          state.claim = null;
        }
      })
      .addCase(likeClaim.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.loading = false;
        state.errors = payload;
      })

      .addCase(dislikeClaim.pending, (state) => { state.loading = true; })
      .addCase(dislikeClaim.fulfilled, (state, action) => {
        const { payload, meta } = action;
        const claimId = meta.arg;
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
        state.claims = state.claims.filter((c) => c.id !== claimId && c._id !== claimId);
        if (state.claim && (state.claim.id === claimId || state.claim._id === claimId)) {
          state.claim = null;
        }
      })
      .addCase(dislikeClaim.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.loading = false;
        state.errors = payload;
      })

      .addCase(markActionsViewed.pending, (state) => {})
      .addCase(markActionsViewed.fulfilled, (state) => {})
      .addCase(markActionsViewed.rejected, (state) => {})

      .addCase(fetchClaimMobileByID.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimMobileByID.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claim = payload;
        state.loading = false;
      })
      .addCase(fetchClaimMobileByID.rejected, (state) => {
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(deleteMessage.pending, (state) => { state.loading = true; })
      .addCase(deleteMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(deleteMessage.rejected, (state, { payload }) => {
        state.loading = false;
        state.errors = payload;
      })

      .addCase(shareMediaToChat.pending, (state) => { state.loading = true; })
      .addCase(shareMediaToChat.fulfilled, (state) => {
        state.loading = false;
        state.isSuccess = true;
      })
      .addCase(shareMediaToChat.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        state.errors = payload;
      });
  }
});

export const { removeClaimLocally, resetUploadProgress } = claimsSlice.actions;
export default claimsSlice.reducer;